import {
	ClientError,
	GraphQLClient,
	RequestOptions,
	Variables,
} from "graphql-request";
import { createClient } from "graphql-ws";
import localforage from "localforage";
import { STORAGE_KEYS } from "./storage-keys";
import { toast, errorToastOptions } from "~served/ui-comps/toast";
import {
	getBusinessErrorDisplayMessage,
	isServedFaultError,
	parseGraphQLBusinessError,
} from "~served/configs/business-error";
import {
	browserWakeSignals,
	createResilientClient,
} from "~served/utils/realtime";
import { queryClient } from "~/providers/query-provider";
import { getState, setState } from "~/store";

type RequestMiddleware = NonNullable<
	GraphQLClient["requestConfig"]["requestMiddleware"]
>;
type GraphQLMiddlewareResponse = Parameters<
	NonNullable<GraphQLClient["requestConfig"]["responseMiddleware"]>
>[0];

export const getAuthHeaders = async () => {
	const obj = {} as Record<string, string>;

	const {
		venueId,
		token: _token,
		locationId,
		deviceId: _deviceId,
	} = getState();
	const token =
		_token ?? (await localforage.getItem<string>(STORAGE_KEYS.TOKEN));
	const deviceId =
		_deviceId ?? (await localforage.getItem<string>(STORAGE_KEYS.DEVICE_ID));

	if (token && obj.Authorization === undefined)
		obj.Authorization = `Bearer ${token}`;
	if (venueId) {
		obj["x-served-venue-id"] = venueId;
		const customerToken = await localforage.getItem<string>(
			STORAGE_KEYS.CUSTOMER_TOKEN(venueId),
		);
		if (customerToken) obj["x-served-customer-token"] = customerToken;
	}
	if (locationId) obj["x-served-location-id"] = locationId;
	if (deviceId) obj["x-served-device-id"] = deviceId;
	obj["x-served-consumer-app-signature"] = new Date().getTime().toString();

	return obj;
};

const requestMiddleware: RequestMiddleware = async (request) => {
	const authHeaders = await getAuthHeaders();

	return { ...request, headers: { ...request.headers, ...authHeaders } };
};

const getReqId = (response: GraphQLMiddlewareResponse): string | undefined => {
	if (response instanceof ClientError) {
		// @ts-ignore
		return response.response.extensions?.reqId as string | undefined;
	}
	return undefined;
};

const responseMiddleware = (response: GraphQLMiddlewareResponse) => {
	if (!(response instanceof ClientError)) return;

	const appError = parseGraphQLBusinessError(response.response.errors?.[0]);
	const message = appError
		? getBusinessErrorDisplayMessage(appError)
		: (response.response.errors?.[0]?.message ?? "Something went wrong!");

	toast.error(
		message,
		errorToastOptions(isServedFaultError(appError), getReqId(response)),
	);
};

export const getHubGqlWsClient = () =>
	createResilientClient({
		createClient,
		url: process.env.HUB_GQL_WS_URL!,
		connectionParams: getAuthHeaders,
		wakeSignals: browserWakeSignals,
		onConnectionChange: (connected) =>
			setState({ isConnectedToServer: connected }),
		// Subscription events published while disconnected are gone; refetch
		// active queries the same way a browser `online` event would.
		onReconnected: () => queryClient.getQueryCache().onOnline(),
	});

export const hubGqlClient = new GraphQLClient(process.env.HUB_GQL_URL!, {
	requestMiddleware,
	responseMiddleware,
});

export const gqlReq = async <
	T extends Record<string, any>,
	V extends Variables = Variables,
>(
	opts: Omit<RequestOptions<V, T>, "signal"> & { signal?: AbortSignal },
): Promise<T[keyof T]> => {
	return hubGqlClient
		.request<T, V>(opts as RequestOptions<V, T>)
		.then((res) => res[Object.keys(res)[0]]);
};
